import { cn } from '@resolute/ui';
import { motion, useReducedMotion } from 'framer-motion';


import { CATEGORY_TABS } from './category-config';

interface CategorySelectorProps {
  categoryId: string;
  onSelect: (id: string) => void;
}

/**
 * Layer 1 — the property-category navigation. A lightweight segmented strip
 * rather than a row of CTA buttons: quiet, transparent tabs on a single subtle
 * glass rail, with a soft highlight that slides between the active category
 * (shared-layout animation). Icon + label read as navigation and stay visually
 * subordinate to the primary actions (Search / Near me). One row on desktop;
 * the rail scrolls horizontally on smaller screens.
 */
export function CategorySelector({ categoryId, onSelect }: CategorySelectorProps) {
  const reduce = useReducedMotion();

  return (
    <div className="w-full">
      <div
        role="group"
        aria-label="Property category"
        className="mx-auto grid w-full max-w-md grid-cols-2 gap-1.5 rounded-xl border border-white/20 bg-slate-950/85 p-1.5 shadow-2xl backdrop-blur-xl sm:flex sm:w-max sm:max-w-none sm:items-center sm:gap-1"
      >
        {CATEGORY_TABS.map((c) => {
          const isActive = categoryId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => onSelect(c.id)}
              className={cn(
                'group relative isolate inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-semibold transition-colors duration-fast ease-out-expo sm:w-auto sm:shrink-0 sm:px-4 sm:py-2 sm:text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
                isActive ? 'text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white',
              )}
            >
              {isActive && (
                <motion.span
                  layoutId={reduce ? undefined : 'hero-cat-pill'}
                  className="absolute inset-0 -z-10 rounded-lg bg-secondary shadow-md ring-1 ring-inset ring-white/25"
                  transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                />
              )}
              <c.Icon
                className={cn(
                  'size-4 shrink-0 transition-transform duration-fast ease-out-expo group-hover:-translate-y-px',
                  isActive ? 'text-white' : 'text-slate-300 group-hover:text-white',
                )}
                aria-hidden="true"
              />
              <span>{c.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
